import { Button, Modal } from "@arco-design/web-react";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { ITINERARY_SERVICE_STANDARD_TYPES } from "../serviceStandardTypes";
import { LEGACY_NOTICE_TEMPLATE_NAMES, LEGACY_NOTICE_TEMPLATES, LEGACY_SERVICE_STANDARD_TEMPLATE, LEGACY_SERVICE_STANDARD_TEMPLATE_NAME } from "../service/legacyRouteTemplates";
import { editableSummary, storedSummary } from "../service/itinerarySummaryService";
import { stripServiceStandardLeadingNumber } from "../service/itineraryOutputService";

type Props = {
  value: Record<string, string[]> | null | undefined;
  onChange: (value: Record<string, string[]>) => void;
  customerNotices?: string | null;
  onNoticesChange?: (value: string) => void;
  onTicketFocus?: () => void;
};

/** 在独立视图中按共享类别编辑全程客户服务标准。 */
export function ItineraryServiceStandards({ value, onChange, customerNotices, onNoticesChange, onTicketFocus }: Props) {
  const [pendingRecords, setPendingRecords] = useState<Record<string, string>>({});
  const [clearConfirmationOpen, setClearConfirmationOpen] = useState(false);
  const [selectedNoticeTemplate, setSelectedNoticeTemplate] = useState("");
  const update = (key: string, records: string[]) => onChange({
    ...Object.fromEntries(
      Object.entries(value ?? {}).map(([entryKey, entryRecords]) => [
        entryKey,
        entryRecords.filter((record) => record.trim()),
      ]),
    ),
    [key]: records.filter((record) => record.trim()),
  });
  const clearPendingRecord = (key: string) => {
    setPendingRecords((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  };
  return (
    <section className="itinerary-content-panel" aria-label="全程服务标准编辑">
      <header className="itinerary-content-heading">
        <div><span>STEP 3 · 全程设置</span><h2>全程服务标准</h2><p>各类服务分别维护，整条行程统一使用。</p></div>
      </header>
      <div className="itinerary-service-grid itinerary-content-body">
        <div className="itinerary-service-template-actions">
          <span>快速模板</span>
          <Button
            size="small"
            aria-label={`应用${LEGACY_SERVICE_STANDARD_TEMPLATE_NAME}服务标准模板`}
            onClick={() => onChange({
              ...value,
              ...Object.fromEntries(Object.entries(LEGACY_SERVICE_STANDARD_TEMPLATE).map(([key, records]) => [key, records.map(stripServiceStandardLeadingNumber)])),
            })}
          >
            {LEGACY_SERVICE_STANDARD_TEMPLATE_NAME}服务标准
          </Button>
          <Button
            size="small"
            status="danger"
            disabled={!Object.values(value ?? {}).some((records) => records.some((record) => record.trim())) && !Object.keys(pendingRecords).length}
            onClick={() => setClearConfirmationOpen(true)}
          >一键清空</Button>
        </div>
        {ITINERARY_SERVICE_STANDARD_TYPES.map(([key, categoryLabel], categoryIndex) => {
          const label = categoryLabel.replace(/标准$/, "");
          const records = (value?.[key] ?? []).filter((record) => record.trim());
          const hasPendingRecord = Object.prototype.hasOwnProperty.call(pendingRecords, key);
          const displayedRecords = hasPendingRecord
            ? [...records, pendingRecords[key]]
            : records;
          return (
            <section className="itinerary-service-section" aria-label={label} key={key}>
              <header>
                <strong>{categoryIndex + 1}、{label}</strong>
                <Button
                  size="mini"
                  icon={<Plus size={14} />}
                  aria-label={`新增${label}`}
                  disabled={hasPendingRecord}
                  onClick={() => setPendingRecords((current) => ({ ...current, [key]: "" }))}
                >
                  新增一条
                </Button>
              </header>
              {displayedRecords.length ? displayedRecords.map((record, index) => {
                const isPendingRecord = hasPendingRecord && index === records.length;
                return (
                  <div className="itinerary-service-row" key={`${key}-${index}`}>
                    <span>{index + 1}</span>
                    <textarea
                      autoFocus={isPendingRecord}
                      aria-label={`${label}第${index + 1}条`}
                      value={stripServiceStandardLeadingNumber(record)}
                      placeholder="输入一条服务标准"
                      onFocus={key === "ticket" ? onTicketFocus : undefined}
                      data-itinerary-ticket-input={key === "ticket" ? "" : undefined}
                      onChange={(event) => {
                        const nextValue = stripServiceStandardLeadingNumber(event.target.value);
                        if (isPendingRecord) {
                          if (!nextValue.trim()) {
                            setPendingRecords((current) => ({ ...current, [key]: nextValue }));
                            return;
                          }
                          clearPendingRecord(key);
                          update(key, [...records, nextValue]);
                          return;
                        }
                        update(key, records.map((item, itemIndex) => itemIndex === index ? nextValue : item));
                      }}
                    />
                    <button
                      type="button"
                      className="itinerary-icon-button itinerary-icon-button--danger"
                      aria-label={`删除${label}第${index + 1}条`}
                      title="删除本条"
                      onClick={() => {
                        if (isPendingRecord) {
                          clearPendingRecord(key);
                          return;
                        }
                        update(key, records.filter((_, itemIndex) => itemIndex !== index));
                      }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                );
              }) : <p className="itinerary-content-empty">暂无记录</p>}
            </section>
          );
        })}
        <section className="itinerary-content-section itinerary-service-notices" aria-label="行程注意事项编辑">
          <header className="itinerary-content-section-heading">
            <div className="itinerary-notice-heading-copy">
              <div className="itinerary-notice-title-row">
                <h3>行程注意事项</h3>
                <label className="itinerary-notice-template-picker">快速模板
                  <select
                    aria-label="注意事项快速模板"
                    value={selectedNoticeTemplate}
                    disabled={!onNoticesChange}
                    onChange={(event) => {
                      const template = event.target.value;
                      setSelectedNoticeTemplate(template);
                      if (template) onNoticesChange?.((LEGACY_NOTICE_TEMPLATES[template] ?? []).join("\n"));
                    }}
                  >
                    <option value="">选择区域模板</option>
                    {LEGACY_NOTICE_TEMPLATE_NAMES.map((name) => <option key={name} value={name}>{name}</option>)}
                  </select>
                </label>
              </div>
              <span>客户分享版会按原内容同步展示；选择模板会替换当前注意事项。</span>
            </div>
            <b>全程统一</b>
          </header>
          <div className="itinerary-content-section-body">
            <label>行程注意事项
              <textarea disabled={!onNoticesChange} value={editableSummary(customerNotices)} onChange={(event) => onNoticesChange?.(storedSummary(event.target.value))} />
            </label>
          </div>
        </section>
      </div>
      <Modal
        title="清空全程服务标准"
        visible={clearConfirmationOpen}
        okText="确认清空"
        cancelText="取消"
        okButtonProps={{ status: "danger" }}
        onCancel={() => setClearConfirmationOpen(false)}
        onOk={() => {
          onChange({});
          setPendingRecords({});
          setClearConfirmationOpen(false);
        }}
      >确认清空全部服务标准？当前草稿中的服务标准和待填写条目将被清除，行程注意事项不受影响。</Modal>
    </section>
  );
}
